import { useEffect, useRef, useState } from 'react';
import { createTts } from '../../lib/tts.js';
import type { Prefs } from '../../lib/types.js';

type Props = {
  prefs: Prefs | null;
  loading: boolean;
  error: string | null;
  setPref: (patch: Partial<Prefs>) => Promise<void>;
};

const SAMPLE = 'The cat sat on the mat';
const tts = createTts();

export function DisplayPrefs({ prefs, loading, error, setPref }: Props): JSX.Element {
  // Local mirror so sliders stay responsive while debounced PUT lands.
  const [local, setLocal] = useState<Prefs | null>(prefs);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const debounceRef = useRef<number | null>(null);
  const pendingRef = useRef<Partial<Prefs>>({});

  // Keep local in sync when server prefs first arrive or change externally.
  useEffect(() => {
    setLocal(prefs);
  }, [prefs]);

  useEffect(() => {
    if (typeof speechSynthesis === 'undefined') return;
    const update = () => {
      const all = speechSynthesis.getVoices();
      setVoices(all.filter((v) => v.lang.startsWith('en')));
    };
    update();
    speechSynthesis.addEventListener('voiceschanged', update);
    return () => speechSynthesis.removeEventListener('voiceschanged', update);
  }, []);

  // Debounced commit. Coalesces multiple patches into a single PUT.
  const queuePatch = (patch: Partial<Prefs>) => {
    pendingRef.current = { ...pendingRef.current, ...patch };
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const next = pendingRef.current;
      pendingRef.current = {};
      void setPref(next);
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const onDelay = (v: number) => {
    if (!local) return;
    setLocal({ ...local, auto_reveal_delay_ms: v });
    queuePatch({ auto_reveal_delay_ms: v });
  };

  const onVoice = (uri: string) => {
    if (!local) return;
    const next = uri === '' ? null : uri;
    setLocal({ ...local, tts_voice: next });
    queuePatch({ tts_voice: next });
  };

  const onRate = (v: number) => {
    if (!local) return;
    setLocal({ ...local, tts_rate: v });
    queuePatch({ tts_rate: v });
  };

  const onPreview = () => {
    tts.cancel();
    tts.speak(SAMPLE, {
      rate: local?.tts_rate ?? 1.0,
      voiceURI: local?.tts_voice ?? null,
    });
  };

  return (
    <section aria-label="显示偏好">
      <h2 className="text-secondary text-hint mb-2">显示偏好</h2>
      <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-5">
        {loading || !local ? (
          <div className="text-secondary text-hint">载入中...</div>
        ) : (
          <>
            <Field label="自动翻面延迟" value={`${local.auto_reveal_delay_ms} ms`}>
              <input
                type="range"
                min={0}
                max={3000}
                step={250}
                value={local.auto_reveal_delay_ms}
                onChange={(e) => onDelay(Number(e.target.value))}
                className="w-full accent-primary"
                aria-label="自动翻面延迟"
              />
            </Field>

            <Field label="朗读语音">
              <select
                value={local.tts_voice ?? ''}
                onChange={(e) => onVoice(e.target.value)}
                className="w-full h-11 bg-surface-2 border border-border rounded-xl px-3 text-primary text-sentence"
                aria-label="朗读语音"
              >
                <option value="">系统默认</option>
                {voices.map((v) => (
                  <option key={v.voiceURI} value={v.voiceURI}>
                    {v.name} · {v.lang}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="朗读速度" value={local.tts_rate.toFixed(2)}>
              <input
                type="range"
                min={0.7}
                max={1.4}
                step={0.05}
                value={local.tts_rate}
                onChange={(e) => onRate(Number(e.target.value))}
                className="w-full accent-primary"
                aria-label="朗读速度"
              />
            </Field>

            <button
              type="button"
              onClick={onPreview}
              className="self-start px-5 h-11 rounded-full border border-border text-primary active:bg-surface-2 text-hint"
            >
              试听
            </button>
            {error ? (
              <div className="text-secondary text-hint break-words">保存失败 · {error}</div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="text-primary text-sentence">{label}</span>
        {value ? <span className="text-secondary text-hint tabular-nums">{value}</span> : null}
      </div>
      {children}
    </div>
  );
}
